import LogoImage from '@assets/svg/standard-logo.svg';
import { useAuth0 } from '@auth0/auth0-react';
import { Loading } from '@components';
import { StudioExpiredAlert } from '@components/StudioExpiredAlert/StudioExpiredAlert';
import { yupResolver } from '@hookform/resolvers/yup';
// import { trackOrganizationSubmitted } from '@services/mixpanel/trackers/UserSessionMetrics';
import { useAppDispatch, useAppSelector } from '@store/redux/store';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCypressHelper } from './hooks/useCypressHelper';
import { useOrgIdQuery } from './hooks/useOrgIdQuery';
import { useOrgQuery } from './hooks/useOrgQuery';

import {
  Body,
  Button,
  Container,
  Description,
  ErrorContainer,
  HelperText,
  Input,
} from './Organization.styles';
import { OrgFormSchema, OrgStates } from './Organization.types';
import { getInitialState } from './utils/getInitialState';
import {
  initialFormState,
  submitWithHook,
  SubmitWithHookArgs,
  schema,
} from './utils/onOrgNameSubmit';
import { signIn } from './utils/signIn';

/**
 *  {@link https://stately.ai/registry/editor/share/eb93bc0c-0b32-4f11-b5b6-576d324ac410 state machine}
 */
const Organization = () => {
  const dispatch = useAppDispatch();
  const { organization } = useAppSelector();
  const { loginWithRedirect } = useAuth0();
  const { register, handleSubmit, watch } = useForm<OrgFormSchema>({
    defaultValues: initialFormState,
    resolver: yupResolver(schema),
  });
  const [orgFormState, setOrgFormState] = useState<OrgStates>(() =>
    getInitialState(organization.orgId),
  );

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const orgIdQ = useOrgIdQuery({
    email: watch('email'),
    isSubmitSuccessful: orgFormState === 'submitted',
    setOrgFormState,
  });

  useEffect(() => {
    if (orgIdQ.data && orgFormState === 'submitted') {
      const studioExpirationDateTime = orgIdQ.data.studio?.paidExpirationDate
        ? new Date(orgIdQ.data.studio.paidExpirationDate).getTime()
        : undefined;
      // Reset back to a state we can handle. Redirecting, which is similar to error as you can submit from it.
      setOrgFormState('redirecting');
      signIn(
        orgIdQ.data.orgId,
        dispatch,
        loginWithRedirect,
        watch('email'),
        studioExpirationDateTime,
      );
    }
  }, [orgIdQ.data, orgFormState]);

  const { isLoading } = useOrgQuery(
    organization,
    orgFormState,
    setOrgFormState,
  );
  useCypressHelper();

  // const expirationDate = data?.studio?.paidExpirationDate
  //   ? data.studio.paidExpirationDate.getTime()
  //   : undefined;
  const { refetch, failureCount } = orgIdQ;
  const args: SubmitWithHookArgs = [setOrgFormState, failureCount, refetch];
  // Studio expired is handled before this is used
  const isNFError = orgFormState === 'error' || orgFormState === 'malicious';

  if (isLoading) {
    return <Loading />;
  }

  if (orgFormState === 'expired') {
    return <StudioExpiredAlert />;
  }

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      handleSubmit(submitWithHook(...args))();
    }
  };

  return (
    <Container
      onSubmit={handleSubmit(submitWithHook(...args))}
      data-testid="login-form"
    >
      <Body>
        <img
          src={LogoImage}
          className="Organization_logo"
          alt="logo"
          height="28px"
        />

        <div style={{ height: 24 }} />
        <div className="Organization_welcomeGroup">
          <Description className="welcome">
            Welcome to the Beam platform!
          </Description>
          <Description className="welcome-extra">
            Please enter your Email.
          </Description>
        </div>

        <div style={{ height: 24 }} />

        <HelperText isFocused={isFocused} htmlFor="orgInput">
          Email address
        </HelperText>
        <Input
          id="orgInput"
          className="orgInput"
          placeholder=""
          {...register('email', {
            required: true,
          })}
          autoFocus
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        {isNFError && (
          <ErrorContainer data-testid="login-error">
            Email not found
          </ErrorContainer>
        )}

        <div style={{ height: 24 }} />

        <Button
          type="submit"
          disabled={orgIdQ.isLoading || orgFormState === 'redirecting'}
        >
          Next
        </Button>

        {/* <div style={{ height: 16 }} /> */}

        {/* {!isQueryError && data && (
          <Continue
            onClick={() => {
              trackOrganizationSubmitted(data.displayName);
              signIn(
                // On a revisit, orgId and exp date have been saved
                organization.orgId,
                dispatch,
                loginWithRedirect,
                watch('email'),
                expirationDate,
              );
            }}
            data-testid="prev-org"
          >{`Continue with ${data.displayName}`}</Continue>
        )} */}
      </Body>
    </Container>
  );
};

export { Organization };
